import { useDisclosure } from "@chakra-ui/hooks";
import DarkButton from "components/actions/DarkButton";
import WhiteButton from "components/actions/WhiteButton";
import Dialog from "components/dialog";
import CheckIcon from "components/icons/Check";
import CloseIcon from "components/icons/CloseIcon";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AttendanceConfirmation from "./confirm-dlg";
import AtriumApi from "api";
import "add-to-calendar-button";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import Loader from "components/loader/Loader";

const ServiceDetails = () => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [showConfirmDlg, setShowConfirmDlg] = useState(false);
  const [userService, setUserService] = useState();

  const [loading, setLoading] = useState(true);

  const params = useParams();
  const [queryParams] = useSearchParams();
  const token = queryParams.get("token");

  useEffect(() => {
    setLoading(true);
    AtriumApi.get(`/v1/volunteer/services/${params.id}?token=${token}`)
      .then((resp) => {
        setUserService(resp.data);
      })
      .catch((error) => {})
      .finally(() => setLoading(false));
  }, [params, token]);

  if (!userService) return;

  const setState = (userServiceId, newState) => {
    return AtriumApi.put(
      `/v1/volunteer/services/${userServiceId}`,
      { state: newState },
      { params: { token } }
    )
      .then((resp) => {
        toast.success("State updated successfully.");
        setUserService(resp.data);
      })
      .catch((error) => {
        toast.error(error?.data?.message || "An error occurred.");
      });
  };

  const confirmRequest = (e) => {
    e.target.setAttribute("disabled", "");
    setState(userService.id, "confirmed").then(() =>
      e.target.removeAttribute("disabled")
    );
    setShowConfirmDlg(false);
  };

  const declineRequest = (e) => {
    e.target.setAttribute("disabled", "");
    setState(userService.id, "declined").then(() =>
      e.target.removeAttribute("disabled")
    );
  };

  let dateString;
  const localDate = dayjs.utc(userService.service.date).local();

  if (userService.service.schedule === "weekly") {
    dateString = "Every " + localDate.format("dddd");
  } else {
    dateString = localDate.format("MMMM D, dddd");
  }

  const showButtons =
    userService?.service?.schedule === "weekly" ||
    (userService?.service?.schedule === "once" &&
      dayjs(
        `${userService?.service?.date.split("T")[0]}T${
          userService?.service?.startAt
        }`
      ).diff(dayjs(), "minute") >= 60);

  return (
    <>
      {loading ? (
        <div className="flex justify-center mt-8">
          <Loader />
        </div>
      ) : (
        <div className="px-4 mt-2">
          <h1 className="text-3xl font-bold">{userService.service.name}</h1>
          <p className="text-sm mt-2">{dateString}</p>
          <p className="text-sm">
            {new Date(
              "2000-01-01 " + userService.service.startAt
            ).toLocaleTimeString([], { hour: "numeric", minute: "numeric" })}
            &nbsp;-&nbsp;
            {new Date(
              "2000-01-01 " + userService.service.endAt
            ).toLocaleTimeString([], { hour: "numeric", minute: "numeric" })}
            &nbsp;@{userService?.service?.address}
          </p>

          <div className="bg-white dark:bg-navy-800 rounded-2xl p-5 mt-6 text-sm">
            <h2 className="font-bold mb-2">Details</h2>
            <p>
              {userService?.service?.description || "No description provided."}
            </p>
          </div>

          {userService.state === "confirmed" && (
            <div className="bg-white dark:bg-navy-800 rounded-3xl p-5 mt-6">
              <span className="inline-block bg-brand-500 text-white rounded-full align-middle mr-2">
                <CheckIcon />
              </span>
              <span className="inline-block align-middle text-sm font-medium">
                You’ve confirmed attendance
              </span>
            </div>
          )}

          {userService.state === "declined" && (
            <div className="bg-white dark:bg-navy-800 rounded-3xl p-5 mt-6">
              <span className="inline-block bg-red-500 p-1 text-white rounded-full align-middle mr-2">
                <CloseIcon />
              </span>
              <span className="inline-block align-middle text-sm font-medium">
                You’ve declined attendance
              </span>
            </div>
          )}

          {showButtons && (
            <div className="flex flex-col lg:flex-row gap-5 my-6">
              {userService.state === "na" && (
                <DarkButton onClick={() => setShowConfirmDlg(true)}>
                  Confirm request
                </DarkButton>
              )}
              {userService.state === "na" && (
                <WhiteButton onClick={declineRequest}>
                  Decline request
                </WhiteButton>
              )}
              {userService.state === "confirmed" && (
                <DarkButton onClick={onOpen}>Add to calendar</DarkButton>
              )}
              {userService.state === "confirmed" && (
                <WhiteButton onClick={() => setState(userService.id, "na")}>
                  Cancel confirmation
                </WhiteButton>
              )}
              {userService.state === "declined" && (
                <WhiteButton onClick={() => setState(userService.id, "na")}>
                  Cancel decline
                </WhiteButton>
              )}
            </div>
          )}
        </div>
      )}

      {showConfirmDlg && (
        <AttendanceConfirmation
          userService={userService}
          onConfirm={confirmRequest}
          onCancel={() => setShowConfirmDlg(false)}
        />
      )}

      <Dialog isOpen={isOpen} onClose={onClose}>
        <h2 className="font-bold text-xl mb-6">Add to calendar</h2>
        <div className="flex flex-col gap-2">
          <add-to-calendar-button
            inline
            buttonStyle="text"
            name={userService.service.name}
            description={userService.service.description}
            startDate={
              new Date(userService.service.date).toISOString().split("T")[0]
            }
            startTime={userService.service.startAt}
            endTime={userService.service.endAt}
            // timeZone="Asia/Shanghai"
            location={userService.service.address}
            availability="free"
            options="'Apple','Google','Outlook.com', 'iCal'"
            trigger="click"
            buttonsList
            customLabels={`{"apple": "Apple Calendar", "google": "Google Calendar", "outlook.com": "Outlook"}`}
            pastDateHandling="disabled"
            recurrence={
              userService.service.schedule === "weekly" ? "weekly" : undefined
            }
          />
        </div>
      </Dialog>
    </>
  );
};

export default ServiceDetails;
